import { useEffect, useMemo, useRef } from 'react'
import type { Tokens } from 'marked'
import { Marked } from 'marked'
import DOMPurify from 'dompurify'
import { cn } from '#/lib/utils'
import { useDocumentDark } from '#/components/common/use-document-dark'
import { enhanceMarkdown } from '#/components/common/markdown-enhance'

/**
 * Markdown renderer (marked + DOMPurify) with:
 * - stable, unique heading ids (anchors for the TOC),
 * - ```mermaid fences marked for client-side rendering,
 * - other fences marked for shiki highlighting + a copy button,
 * - tables wrapped in a horizontally scrolling container.
 * Enhancements (mermaid/shiki/copy) run after mount in `enhanceMarkdown` — never during SSR.
 */

export function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .slice(0, 80) || 'section'
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Plain text of inline tokens (for slugs and the TOC). */
function tokensText(tokens: Tokens.Generic[] | undefined): string {
  if (!tokens) return ''
  return tokens.map((t) => ('tokens' in t && Array.isArray(t.tokens) ? tokensText(t.tokens as Tokens.Generic[]) : 'text' in t ? String((t as { text?: string }).text ?? '') : '')).join('')
}

export interface Heading { id: string; text: string; depth: number }

/** Extract h1–h3 headings with the same ids the renderer emits. */
export function extractHeadings(content: string): Heading[] {
  const seen = new Map<string, number>()
  const out: Heading[] = []
  for (const tok of new Marked({ gfm: true }).lexer(content)) {
    if (tok.type !== 'heading') continue
    const h = tok as Tokens.Heading
    if (h.depth > 3) continue
    const text = tokensText(h.tokens).trim()
    out.push({ id: uniqueId(slugify(text), seen), text, depth: h.depth })
  }
  return out
}

function uniqueId(base: string, seen: Map<string, number>) {
  const n = seen.get(base) ?? 0
  seen.set(base, n + 1)
  return n === 0 ? base : `${base}-${n + 1}`
}

function createRenderer() {
  const seen = new Map<string, number>()
  const md = new Marked({ gfm: true, breaks: false })
  md.use({
    renderer: {
      heading({ tokens, depth }) {
        const text = tokensText(tokens).trim()
        const id = depth <= 3 ? uniqueId(slugify(text), seen) : slugify(text)
        return `<h${depth} id="${id}">${this.parser.parseInline(tokens)}</h${depth}>\n`
      },
      code({ text, lang }) {
        const l = (lang ?? '').trim().split(/\s+/)[0]?.toLowerCase() ?? ''
        if (l === 'mermaid') return `<pre data-mermaid="1"><code class="language-mermaid">${escapeHtml(text)}</code></pre>\n`
        return `<pre data-code="1" data-lang="${escapeHtml(l || 'text')}"><code class="language-${escapeHtml(l || 'text')}">${escapeHtml(text)}</code></pre>\n`
      },
      table(token) {
        // reuse the default table renderer output, wrapped for horizontal scrolling
        const header = token.header.map((c) => `<th${c.align ? ` align="${c.align}"` : ''}>${this.parser.parseInline(c.tokens)}</th>`).join('')
        const rows = token.rows.map((r) => `<tr>${r.map((c) => `<td${c.align ? ` align="${c.align}"` : ''}>${this.parser.parseInline(c.tokens)}</td>`).join('')}</tr>`).join('')
        return `<div class="md-table"><table><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table></div>\n`
      },
    },
  })
  return md
}

// external links open in a new tab; runs once per module load on the client
if (typeof window !== 'undefined') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      const href = node.getAttribute('href') ?? ''
      if (href.startsWith('#')) {
        node.removeAttribute('target')
        node.removeAttribute('rel')
      } else {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    }
  })
}

export function renderMarkdownHtml(content: string) {
  const raw = createRenderer().parse(content, { async: false })
  if (typeof window === 'undefined') return raw
  return DOMPurify.sanitize(raw, { ADD_ATTR: ['target', 'align'] })
}

export function Markdown({ content, className }: { content: string; className?: string }) {
  const html = useMemo(() => renderMarkdownHtml(content), [content])
  // React 19 re-applies innerHTML whenever this object's identity changes — keep it stable so DOM enhancements survive re-renders
  const inner = useMemo(() => ({ __html: html }), [html])
  const ref = useRef<HTMLDivElement>(null)
  const dark = useDocumentDark()
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ctrl = new AbortController()
    void enhanceMarkdown(el, { dark, signal: ctrl.signal })
    return () => {
      ctrl.abort()
    }
  }, [html, dark])
  return (
    <div
      ref={ref}
      className={cn(
        'md prose prose-sm max-w-none dark:prose-invert prose-headings:font-heading prose-headings:tracking-tight prose-headings:scroll-mt-16 prose-pre:bg-muted prose-pre:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary',
        className,
      )}
      dangerouslySetInnerHTML={inner}
    />
  )
}
